import React from 'react';
import { MapPin, Clock, Info, Bike, ShoppingBag, UtensilsCrossed } from 'lucide-react';
import { Restaurant, RestaurantSettings } from '../../lib/types';
import { formatCurrency } from '../../lib/utils';
import { LanguageSelector } from '../ui/LanguageSelector';
import { useLanguage } from '../../lib/i18n';
interface RestaurantHeaderProps {
  restaurant: Restaurant;
  settings: RestaurantSettings | null;
  zoneName?: string;
}
export function RestaurantHeader({
  restaurant,
  settings,
  zoneName
}: RestaurantHeaderProps) {
  const {
    t
  } = useLanguage();
  const isEmilius = restaurant.slug === 'emilius-pizza';
  const isKabu = restaurant.slug === 'kabu';
  const coverUrl = isEmilius ? 'https://cdn.magicpatterns.com/uploads/9961Lp77JNxDVSaBa43esn/Screenshot_2026-04-05_at_11.27.46_AM.png' : isKabu ? 'https://cdn.magicpatterns.com/uploads/9XbgtFaH2YhrgJYD7LjTSs/Diseno_minimalista_con_logo_moderno.png' : restaurant.cover_url || 'https://cdn.magicpatterns.com/uploads/rYDkPPUtvynEvWDL4HujUk/portada.png';
  const logoUrl = isEmilius ? 'https://cdn.magicpatterns.com/uploads/9961Lp77JNxDVSaBa43esn/Screenshot_2026-04-05_at_11.27.46_AM.png' : isKabu ? 'https://cdn.magicpatterns.com/uploads/9XbgtFaH2YhrgJYD7LjTSs/Diseno_minimalista_con_logo_moderno.png' : restaurant.logo_url;
  return <div className="bg-[#0B0B0B] border-b border-white/10" data-id="element-1260">
      {/* Cover Image */}
      <div className="relative h-44 sm:h-60 md:h-72 w-full bg-[#141414]" data-id="element-1261">
        <img src={coverUrl} alt={restaurant.name} className="w-full h-full object-cover" data-id="element-1262" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-black/40 to-transparent" data-id="element-1263" />

        {/* Badges */}
        <div className="absolute top-4 right-4 flex flex-col gap-2 items-end" data-id="element-1264">
          <div className="bg-[#0B0B0B]/80 backdrop-blur-md rounded-lg shadow-lg" data-id="element-1265">
            <LanguageSelector data-id="element-1266" />
          </div>
          {restaurant.is_featured && <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-[#C9A34E] text-[#0B0B0B] shadow-lg" data-id="element-1267">
              ★ {t('featured')}
            </span>}
          {!restaurant.is_open && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-[#CE1126] text-white shadow-lg" data-id="element-1268">
              {t('closed')}
            </span>}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-1269">
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 sm:items-end -mt-10 sm:-mt-14 mb-5" data-id="element-1270">
          {/* Logo */}
          <div className="relative w-20 h-20 sm:w-28 sm:h-28 rounded-2xl bg-[#0B0B0B] p-1 shadow-xl border border-white/10 shrink-0" data-id="element-1271">
            {logoUrl ? <img src={logoUrl} alt={`${restaurant.name} logo`} className={`w-full h-full rounded-xl ${isEmilius ? 'object-contain bg-[#F2E6D5]' : isKabu ? 'object-contain bg-[#E0B87B]' : 'object-cover'}`} data-id="element-1272" /> : <img src="https://cdn.magicpatterns.com/uploads/66K7i7HxpjApVZJyeR9rjp/ChatGPT_Image_15_mar_2026,_13_53_22.png" alt="NOIZA" className="w-full h-full rounded-xl object-cover" data-id="element-1273" />}
          </div>

          {/* Title & Info */}
          <div className="flex-1 pb-2" data-id="element-1274">
            <h1 className="text-2xl sm:text-4xl font-black text-white mb-1 tracking-tight" data-id="element-1275">
              {restaurant.name}
            </h1>
            {restaurant.description_short && <p className="text-white/50 text-sm sm:text-base max-w-2xl mb-3" data-id="element-1276">
                {restaurant.description_short}
              </p>}

            <div className="flex flex-wrap items-center gap-3 text-sm text-white/40 font-medium" data-id="element-1277">
              {(zoneName || restaurant.address_text) && <div className="flex items-center gap-1.5" data-id="element-1278">
                  <MapPin className="w-4 h-4" data-id="element-1279" />
                  {zoneName || restaurant.address_text}
                </div>}

              {settings?.average_prep_time_minutes && <div className="flex items-center gap-1.5" data-id="element-1280">
                  <Clock className="w-4 h-4" data-id="element-1281" />
                  {settings.average_prep_time_minutes} min
                </div>}

              {settings?.minimum_order_amount != null && settings.minimum_order_amount > 0 && <div className="flex items-center gap-1.5" data-id="element-1282">
                    <Info className="w-4 h-4" data-id="element-1283" />
                    Mín. {formatCurrency(settings.minimum_order_amount)}
                  </div>}
            </div>
          </div>
        </div>

        {/* Order Methods — Mexican color gradient badges */}
        {settings && <div className="flex flex-wrap gap-2.5 pb-5" data-id="element-1284">
            {settings.accept_delivery && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white border border-white/10" style={{
          background: 'linear-gradient(135deg, #006847 0%, #2AABB3 100%)'
        }} data-id="element-1285">
                <Bike className="w-4 h-4" data-id="element-1286" /> {t('delivery')}
              </span>}
            {settings.accept_pickup && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white border border-white/10" style={{
          background: 'linear-gradient(135deg, #CE1126 0%, #E8751A 100%)'
        }} data-id="element-1287">
                <ShoppingBag className="w-4 h-4" data-id="element-1288" /> {t('pickup')}
              </span>}
            {(settings as any).accept_dine_in && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white border border-white/10" style={{
          background: 'linear-gradient(135deg, #6B2FA0 0%, #CE1126 100%)'
        }} data-id="element-1289">
                <UtensilsCrossed className="w-4 h-4" data-id="element-1290" /> {t('dine_in')}
              </span>}
          </div>}
      </div>
    </div>;
}